import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

export function CreateLeadModal({ open, onClose, onSuccess, editLead }) {
    const [formData, setFormData] = useState({
        firstName: editLead?.name?.split(" ")[0] || "",
        lastName: editLead?.name?.split(" ")?.slice(1).join(" ") || "",
        email: editLead?.email || "",
        phone: editLead?.phone || "",
        secondPhone: editLead?.secondPhone || "",
        status: editLead?.status || "New",
        desk: editLead?.desk || "",
        source: editLead?.source || "",
        country: editLead?.country || "",
        city: editLead?.city || "",
        address: editLead?.address || "",
        lastComment: editLead?.lastComment || "",
        extra: editLead?.extra || ""
    });

    const statuses = ["New", "Interested", "Follow-up", "Converted", "Not Answered"];
    const desks = ["Sales Desk A", "Sales Desk B", "Retention Desk", "VIP Desk"];
    const deskManagers = ["John Smith", "Sarah Johnson", "Mike Wilson", "Emily Davis"];
    const countries = ["USA", "UK", "Germany", "France", "Spain", "Italy", "Canada"];

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate that either firstName or lastName is provided
        const hasFirstName = formData.firstName && formData.firstName.trim() !== "";
        const hasLastName = formData.lastName && formData.lastName.trim() !== "";
        if (!hasFirstName && !hasLastName) {
            toast.error("First Name or Last Name is required");
            return;
        }

        if (!formData.email) {
            toast.error("Email is required");
            return;
        }

        // Generate a default password when creating a new lead (email + timestamp if email exists, otherwise random string)
        const defaultPassword = editLead 
            ? "" 
            : (formData.email 
                ? `${formData.email}_${Date.now()}` 
                : `lead_${Date.now()}_${Math.random().toString(36).substring(7)}`);

        const leadData = {
            id: editLead?.id || `LEAD-${Date.now()}`,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            email: formData.email,
            phone: formData.phone,
            secondPhone: formData.secondPhone,
            password: editLead ? "" : defaultPassword,
            status: formData.status,
            desk: formData.desk,
            source: formData.source,
            country: formData.country,
            city: formData.city,
            address: formData.address,
            extra: formData.extra,
            // When creating a new lead, copy extra field content to lastComment
            lastComment: editLead ? formData.lastComment : (formData.extra ? formData.extra : formData.lastComment || "")
        };

        onSuccess(leadData);
        toast.success(editLead ? "Lead updated successfully!" : "Lead created successfully!");
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900">{editLead ? "Edit Lead" : "Create New Lead"}</DialogTitle>
                    <DialogDescription className="text-gray-600">
                        {editLead
                            ? "Update the lead information below."
                            : "Fill in the information below to create a new lead."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                    {/* Basic Information */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm space-y-3">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="firstName" className="text-blue-800 font-medium">First Name <span className="text-blue-500 text-sm">(or Last Name required)</span></Label>
                            <Input
                                id="firstName"
                                value={formData.firstName}
                                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                                className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="lastName" className="text-blue-800 font-medium">Last Name <span className="text-blue-500 text-sm">(or First Name required)</span></Label>
                            <Input
                                id="lastName"
                                value={formData.lastName}
                                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                                className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="phone" className="text-blue-800 font-medium">Phone Number</Label>
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                                className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="secondPhone" className="text-blue-800 font-medium">Second Phone</Label>
                            <Input
                                id="secondPhone"
                                value={formData.secondPhone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, secondPhone: e.target.value }))}
                                className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                            />
                        </div>
                    </div>

                    {/* Location Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="country">Country</Label>
                            <Select
                                options={countries.map((c) => ({ label: c, value: c }))}
                                value={formData.country}
                                onChange={(value) => setFormData((prev) => ({ ...prev, country: value }))}
                                placeholder="Select country"
                                searchable
                            />
                        </div>
                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input
                                id="city"
                                value={formData.city}
                                onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="address">Address</Label>
                            <Input
                                id="address"
                                value={formData.address}
                                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label htmlFor="status">Status</Label>
                            <Select
                                options={statuses.map((s) => ({ label: s, value: s }))}
                                value={formData.status}
                                onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                                placeholder="Select status"
                                searchable
                            />
                        </div>
                    </div>

                    {/* Assignment Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="deskManager">Manager</Label>
                            <Select
                                options={deskManagers.map((m) => ({ label: m, value: m }))}
                                value={formData.deskManager}
                                onChange={(value) => setFormData((prev) => ({ ...prev, deskManager: value }))}
                                placeholder="Select manager"
                                searchable
                            />
                        </div>
                        <div>
                            <Label htmlFor="desk">Desk</Label>
                            <Select
                                options={desks.map((d) => ({ label: d, value: d }))}
                                value={formData.desk}
                                onChange={(value) => setFormData((prev) => ({ ...prev, desk: value }))}
                                placeholder="Select desk"
                                searchable
                            />
                        </div>
                    </div>

                    {/* Source Information */}
                    <div>
                        <Label htmlFor="source">Source</Label>
                        <Input
                            id="source"
                            type="text"
                            value={formData.source}
                            onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
                        />
                    </div>

                    <div>
                        <Label htmlFor="extra" className="text-blue-800 font-medium">Extra Notes</Label>
                        <Textarea
                            id="extra"
                            value={formData.extra}
                            onChange={(e) => setFormData((prev) => ({ ...prev, extra: e.target.value }))}
                            rows={2}
                            className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white"
                        />
                    </div>

                    <div className="flex space-x-3 pt-4 border-t border-gray-200">
                        <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700:bg-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-200">
                            {editLead ? "Update Lead" : "Create Lead"}
                        </Button>
                        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
